import React, { useCallback, useEffect, useId, useRef, useState } from 'react';

import type { DrawReactComponentProps } from '@aline/common';
import { pxToTwips, twipsToPx } from '@aline/common';
import { makeVar } from '@apollo/client';
import { useReactiveVar } from '@apollo/client/react';
import { ClientTransforms } from 'src/editor/transforms';
import { suspendSelectionScroll } from 'src/editor/transforms/scroll-transforms';
import { useCurrentUserId } from 'src/gql';
import { useAuthorizedTransforms, useOverrideGlobalCursor } from 'src/hooks';
import { useEditor } from 'src/layout-engine';
import { useCurrentEditingMode, useEditingDisabled } from 'src/store/document/selectors';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles<{ isActive: boolean }>()((theme, { isActive }) => ({
  tableBorderResizer: {
    background: isActive ? theme.appColors.primary : undefined,
    cursor: 'col-resize',
    transform: 'translateX(-50%)',
    transition: 'background 150ms ease',
  },
}));

export function TableBorderResizer({
  borderWidth,
  cMTablePath,
  columnEdgeIndex,
  height,
}: DrawReactComponentProps['TableBorderResizer']) {
  const columnEdgeId = `${cMTablePath}-${columnEdgeIndex}`;

  const editor = useEditor();
  const cannotEdit = useEditingDisabled();
  const currentUserId = useCurrentUserId();
  const editingMode = useCurrentEditingMode();
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const { isColumnEdgeActive, isDragging, setIsDragging, setIsHovering } = useActiveResizerState({
    columnEdgeId,
  });
  const { classes } = useStyles({ isActive: isColumnEdgeActive });

  const lastBorderClientX = useRef<number>(null);

  useOverrideGlobalCursor(isDragging ? 'col-resize' : null);

  useEffect(() => {
    if (isDragging) {
      function handleMouseMove(event: MouseEvent) {
        if (lastBorderClientX.current === null) return;

        const requestedDeltaPx = event.clientX - lastBorderClientX.current;
        if (requestedDeltaPx === 0) return;

        if (currentUserId && editingMode) {
          const requestedDeltaTwips = pxToTwips(requestedDeltaPx);

          suspendSelectionScroll();
          const appliedDeltaTwips = AuthorizedTransforms.resizeTableColumns(editor, {
            authorId: currentUserId,
            cMTablePath,
            columnEdgeIndex,
            editingMode,
            widthDelta: requestedDeltaTwips,
          });

          // If nothing was applied (e.g. at minimum width), keep the virtual border position unchanged
          // so further movement in that direction has no effect until the cursor comes back across the handle.
          if (appliedDeltaTwips === 0) return;

          const appliedDeltaPx = twipsToPx(appliedDeltaTwips);
          lastBorderClientX.current = lastBorderClientX.current + appliedDeltaPx;
        }
      }

      function handleMouseUp() {
        setIsDragging(false);
        ClientTransforms.focus(editor);
      }

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [
    AuthorizedTransforms,
    borderWidth,
    cMTablePath,
    columnEdgeIndex,
    currentUserId,
    isDragging,
    editingMode,
    editor,
    setIsDragging,
  ]);

  if (cannotEdit) return null;

  return (
    <div
      className={classes.tableBorderResizer}
      style={{ height, width: Math.max(borderWidth, 4) }}
      onMouseDown={event => {
        // Prevent the page from intercepting the mouse down event
        event.preventDefault();
        event.stopPropagation();
        suspendSelectionScroll();
        lastBorderClientX.current = event.clientX;
        setIsDragging(true);
      }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    />
  );
}

/**
 * An active resizer entry. A resizer is active if it's currently being hovered or dragged. A column
 * edge is active if the earliest active resizer belongs to that column.
 */
interface ActiveResizer {
  columnEdgeId: string;
  resizerId: string;
}

const activeResizersVar = makeVar<ActiveResizer[]>([]);

function useActiveResizerState({ columnEdgeId }: { columnEdgeId: string }) {
  const resizerId = useId();
  const activeResizers = useReactiveVar(activeResizersVar);
  const isColumnEdgeActive = activeResizers?.at(0)?.columnEdgeId === columnEdgeId;

  const [isHovering, baseSetIsHovering] = useState(false);
  const [isDragging, baseSetIsDragging] = useState(false);
  const isActive = isHovering || isDragging;

  const setIsActive = useCallback(
    (nextIsActive: boolean) => {
      const prevIsActive = isActive;

      if (!prevIsActive && nextIsActive) {
        activeResizersVar([...activeResizersVar(), { columnEdgeId, resizerId }]);
      }

      if (prevIsActive && !nextIsActive) {
        activeResizersVar(
          activeResizersVar().filter(activeResizer => activeResizer.resizerId !== resizerId)
        );
      }
    },
    [isActive, columnEdgeId, resizerId]
  );

  // Page virtualization can unmount a resizer mid-drag; clear global active state in that case.
  useEffect(() => {
    return () => {
      activeResizersVar(
        activeResizersVar().filter(activeResizer => activeResizer.resizerId !== resizerId)
      );
    };
  }, [resizerId]);

  const setIsHovering = useCallback(
    (value: boolean) => {
      baseSetIsHovering(value);
      setIsActive(value || isDragging);
    },
    [isDragging, setIsActive]
  );

  const setIsDragging = useCallback(
    (value: boolean) => {
      baseSetIsDragging(value);
      setIsActive(value || isHovering);
    },
    [isHovering, setIsActive]
  );

  return { isColumnEdgeActive, isDragging, setIsDragging, setIsHovering };
}
